import * as Accordion from "@radix-ui/react-accordion"
import { useAppDispatch } from "@redux/app/hooks"
import { APP_STRUCTURE_QUERY_KEY } from "@redux/features/appStructure/queries"
import { useQueryClient } from "@tanstack/react-query"
import { Button } from "@ui-lib/index"
import { common } from "@utils/common"
import { requestApi2 } from "@utils/http"
import { endpoints, JsonData } from "api"
import { useEffect, useRef, useState } from "react"
import { MdInfoOutline, MdOutlineChevronRight } from "react-icons/md"
import { z } from "zod"
import PlantSelector from "./PlantSelector"

const CapacityName = z.enum(["Deadweight", "Gross tonnage", "Bollard pull", "Cargo hold"])

export type CapacityName = z.infer<typeof CapacityName>

const CapacityUnit = z.enum(["t", "m³", ""])
export type CapacityUnit = z.infer<typeof CapacityUnit>

const FilterGroup = z.enum(["UniSea"])
export type FilterGroup = z.infer<typeof FilterGroup>

export const vesselMetadataTypeParser = z
	.object({
		"Vessel built": z.nullable(z.string()),
		"Vessel design": z.nullable(z.array(z.string())),
		"Class definition": z.nullable(z.array(z.string())),
		"Length o.a.": z.nullable(z.string()),
		"Length b.p.": z.nullable(z.string()),
		"Breadth mld": z.nullable(z.string()),
		Bext: z.nullable(z.string()),
		Depth: z.nullable(z.string()),
		"Design draft": z.nullable(z.string()),
		"Summer draft": z.nullable(z.string()),
		"Scantling draft": z.nullable(z.string()),
		Accommodation: z.nullable(z.string()),
		"Service speed": z.nullable(z.string()),
		Charterer: z.nullable(z.string()),
		CapacityName: z.nullable(CapacityName),
		CapacityUnit: z.nullable(CapacityUnit),
		filterGroup: z.nullable(FilterGroup),
		isUnderConfiguration: z.nullable(z.boolean()),
		additionalProps: z.nullable(z.record(z.any())),
	})
	.strict()

export const partialVesselMetadataTypeParser = vesselMetadataTypeParser.partial().strict()
export type PartialMetadataObject = z.infer<typeof partialVesselMetadataTypeParser>

const configTemplate: PartialMetadataObject = {
	"Vessel built": "",
	"Vessel design": [],
	"Class definition": [],
	"Length o.a.": "",
	"Length b.p.": "",
	"Breadth mld": "",
	Bext: "",
	Depth: "",
	"Design draft": "",
	"Summer draft": "",
	"Scantling draft": "",
	Accommodation: "",
	"Service speed": "",
	Charterer: "",
	CapacityName: "Deadweight",
	filterGroup: undefined,
	isUnderConfiguration: false,
	additionalProps: {},
}
export default function PlantMetadata() {
	const [errorJson, setErrorJson] = useState<string | null>(null)
	const [errorType, setErrorType] = useState<string | null>(null)
	const queryClient = useQueryClient()

	const dispatch = useAppDispatch()

	const [plantsList, setPlantsList] = useState<
		{ name: string; id: number; metaData: JsonData | null }[]
	>(
		Array.from(common.plantDataFromName.values())
			.map((plant) => {
				return { name: plant.display_name, id: plant.id, metaData: plant.metaData }
			})
			.sort((a, b) => a.name.localeCompare(b.name))
	)
	const [selectedPlant, setSelectedPlant] = useState(
		Array.from(common.plantDataFromName.values())
			.map((plant) => {
				return { name: plant.display_name, id: plant.id, metaData: plant.metaData }
			})
			.sort((a, b) => a.name.localeCompare(b.name))?.[0]?.id
	)
	const [textAreaText, setTextAreaText] = useState("{}")

	useEffect(() => {
		if (selectedPlant === undefined) {
			return
		}
		setErrorType(null)
		setErrorJson(null)
		const metaData = plantsList.find((plant) => plant.id === selectedPlant)?.metaData

		setTextAreaText(JSON.stringify(metaData === null ? {} : metaData, null, 4))
	}, [selectedPlant])

	const refetch = async () => {
		await queryClient.refetchQueries({
			queryKey: APP_STRUCTURE_QUERY_KEY,
		})
	}
	const handleUpdate = async () => {
		if (selectedPlant === undefined) {
			return
		}
		try {
			const parsedText = JSON.parse(textAreaText)
			const valiDateText = partialVesselMetadataTypeParser.parse(parsedText)
			await requestApi2(endpoints.plantSetMetaInfoJson, {
				plantId: selectedPlant,
				data: valiDateText,
			})
			setPlantsList((prev) =>
				prev.map((plant) =>
					plant.id === selectedPlant ? { ...plant, metaData: valiDateText } : plant
				)
			)
			console.log("Metadata updated successfully")

			refetch()
		} catch (e) {
			console.error("Error parsing JSON", e)
			alert("Error parsing JSON " + e)
		}
	}
	const handleTemplateSetter = () => {
		if (selectedPlant === undefined) {
			return
		}
		checkForErrors(JSON.stringify(configTemplate))
		setTextAreaText(JSON.stringify(configTemplate, null, 4))
	}
	const handleDiscardCahnges = () => {
		if (selectedPlant === undefined) {
			return
		}
		checkForErrors(JSON.stringify(plantsList.find((p) => p.id === selectedPlant)?.metaData))
		setTextAreaText(
			JSON.stringify(plantsList.find((p) => p.id === selectedPlant)?.metaData, null, 4)
		)
	}
	const handleFormatJson = () => {
		try {
			const parsed = JSON.parse(textAreaText)
			const formatted = JSON.stringify(parsed, null, 4)
			setTextAreaText(formatted)
		} catch (e) {
			setErrorJson(`${e}`)
		}
	}
	const checkForErrors = (text: string) => {
		try {
			const parsedText = JSON.parse(text)
			try {
				setErrorJson(null)
				partialVesselMetadataTypeParser.parse(parsedText)
				setErrorType(null)
			} catch (e) {
				setErrorType(`${e}`)
			}
		} catch (e) {
			setErrorJson(`${e}`)
		}
	}

	let parsedText = "{}"
	try {
		parsedText = JSON.parse(textAreaText)
	} catch (error) {}
	const r = vesselMetadataTypeParser.safeParse(parsedText)

	const [isAccordionOpen, setIsAccordionOpen] = useState<string>("")
	const accordionRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			if (
				accordionRef.current !== null &&
				!accordionRef.current.contains(event.target as Node)
			) {
				setIsAccordionOpen("")
			}
		}
		document.addEventListener("mousedown", handleClickOutside)
		return () => document.removeEventListener("mousedown", handleClickOutside)
	}, [])

	return (
		<div className={`mt-3 w-full border-t-1 pt-4`}>
			<div className={`flex flex-col gap-2 border-1 border-slate-300 p-2`}>
				<div className={`flex flex-row items-center gap-2`}>
					<div className={`flex w-full flex-row justify-between`}>
						<div className={`flex flex-row gap-4`}>
							<p className={`text-body pr-4 font-semibold`}>Update plant metadata</p>
							<PlantSelector
								options={plantsList.map((p) => {
									return {
										value: p.id,
										label: p.name,
										color:
											p.metaData === null ||
											JSON.stringify(p.metaData) === `{}`
												? "#e7000b"
												: vesselMetadataTypeParser.safeParse(p.metaData)
															.success === false
													? "#d08700"
													: undefined,
									}
								})}
								value={{
									value: plantsList.find((p) => p.id === selectedPlant)?.id ?? -1,
									label:
										plantsList.find((p) => p.id === selectedPlant)?.name ??
										"unknown plant",
									color:
										plantsList.find((p) => p.id === selectedPlant)?.metaData ===
											null ||
										JSON.stringify(
											plantsList.find((p) => p.id === selectedPlant)?.metaData
										) === `{}`
											? "#e7000b"
											: vesselMetadataTypeParser.safeParse(
														plantsList.find(
															(p) => p.id === selectedPlant
														)?.metaData
												  ).success === false
												? "#d08700"
												: undefined,
								}}
								onChange={(e) => setSelectedPlant(e?.value)}
							/>
						</div>
						<div className={`flex flex-row items-center gap-4`}>
							<Button
								onClick={handleDiscardCahnges}
								variant={"destructive"}
								disabled={
									textAreaText ===
										JSON.stringify(
											plantsList.find((p) => p.id === selectedPlant)?.metaData
										) || textAreaText === JSON.stringify({})
								}
							>
								<Button.Label>Discard changes</Button.Label>
							</Button>
							<Button
								onClick={handleFormatJson}
								variant={"outline-dark"}
								disabled={errorJson !== null}
							>
								<Button.Label>Format</Button.Label>
							</Button>
							<Button
								onClick={handleTemplateSetter}
								variant={"outline-dark"}
							>
								<Button.Label>Metadata template</Button.Label>
							</Button>

							<Button
								onClick={handleUpdate}
								disabled={
									textAreaText ===
										JSON.stringify(
											plantsList.find((p) => p.id === selectedPlant)?.metaData
										) || textAreaText === JSON.stringify({})
								}
							>
								<Button.Label>Update</Button.Label>
							</Button>
						</div>
					</div>
				</div>

				{errorJson !== null || errorType !== null ? (
					<div
						className={`flex w-fit flex-row items-center gap-2 rounded-sm border-1 border-red-700 bg-red-50 p-1 text-red-700`}
					>
						<MdInfoOutline className={`h-5 w-5`} />
						<p className={`text-button font-medium`}>
							{errorJson !== null ? errorJson : errorType}
						</p>
					</div>
				) : r.success === false && errorJson === null ? (
					<Accordion.Root
						ref={accordionRef}
						type="single"
						collapsible
						value={isAccordionOpen}
						onValueChange={setIsAccordionOpen}
					>
						<Accordion.Item
							value="item-1"
							className="text-button w-fit"
						>
							<Accordion.Trigger
								className={`group align-center flex w-full flex-row justify-between rounded-sm border-1 border-amber-700 bg-amber-100/75 p-1 text-amber-700 hover:bg-amber-100 data-[state=open]:rounded-b-none`}
							>
								<div className={`flex flex-row items-center gap-2`}>
									<MdInfoOutline className={`h-5 w-5`} />
									<p className={`font-semibold`}>{"Unspecified props"}</p>
								</div>
								<MdOutlineChevronRight
									className="h-6 w-6 rotate-90 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:-rotate-90"
									aria-hidden
								/>
							</Accordion.Trigger>
							<Accordion.Content>
								<ul
									className={`absolute mt-1 border-1 border-amber-700 bg-amber-50 px-4 py-2`}
								>
									{r.error.errors.map((err) => (
										<li
											key={err.path.join(".")}
											className="font-semibold text-amber-600"
										>
											{err.path.join(".")}:{" "}
											<span className={`font-normal`}>
												{err.code === "invalid_type"
													? `${err.expected} | null`
													: err.code}
											</span>
										</li>
									))}
								</ul>
							</Accordion.Content>
						</Accordion.Item>
					</Accordion.Root>
				) : null}

				<textarea
					value={textAreaText}
					cols={100}
					rows={20}
					onChange={(event) => {
						checkForErrors(event.target.value)
						setTextAreaText(event.target.value)
					}}
				/>
			</div>
		</div>
	)
}
